import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import FileManager from "../../containers/pages/CANshare/components/FileManager";
import {
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";
import Viewer from "containers/pages/CANshare/components/Viewer";
import styles from "./index.module.css";
import useFileManager from "containers/pages/CANshare/hooks/useFileManager";
import {
    ArrowBackIcon,
    ClearIcon,
    ContentCopyIcon,
    ContentCutIcon,
    CreateNewFolderIcon,
    DeleteIcon,
    DownloadIcon,
    DriveFileRenameIcon,
    GridViewOutlinedIcon,
    SecurityOutlinedIcon,
    UploadFileOutlinedIcon,
    ViewListOutlinedIcon,
    SettingsOutlinedIcon,
} from "components/icons";
import FileMgrBreadcrumb from "containers/pages/CANshare/components/FileMgrBreadcrumbs";
import { handleFileDownload } from "libs/utils/file";
import {
    createNewFolder,
    getDirectoryPermissions,
    moveDirectory,
    moveFileToDirectory,
    setDirectoryPermissions,
} from "api/canshare/canshareApi";
import { useNotificationProvider } from "hooks";
import useContextMenu from "containers/pages/CANshare/hooks/useContextMenu";
import ActionMenu from "containers/pages/CANshare/components/ActionMenu";
import UppyDashboard from "components/Uppy/UppyDashboard";
import useDialog from "containers/pages/CANshare/hooks/useDialog";
import Permissions from "containers/pages/CANshare/components/Permissions";
import DeleteDialog from "containers/pages/CANshare/components/DeleteDialog";
import { ProgressCircular } from "components";
import ClientAccessPoint from "containers/pages/CANshare/components/ClientAccessPoint";
import { selectUserInfo } from "redux/userInfo/userInfoSlice"; // adjust as needed

const CanShare = () => {
    // Get current user info for the inline check
    const userInfo = useSelector(selectUserInfo);
    console.log("userInfo :>> ", userInfo);
    // Since the user info is wrapped in userInfo, extract userId from that nested object.
    const userId = userInfo?.userInfo?.userId;
    console.log("Extracted userId:", userId); // Should print "96"

    const [activeDoc, setActiveDoc] = useState(undefined);
    const [openFilePreview, setOpenFilePreview] = useState(false);
    const { notifyError, notifyWarning, notifySuccess } =
        useNotificationProvider();

    const [tileMode, setTileMode] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const FileMgrProps = useFileManager();
    const {
        setFileList,
        currDirId,
        checked,
        historyRef,
        traverseBackDirectory,
        custId,
        setCustId,
        copyMoveAction,
        copyMoveClipboard,
        addFileToCopyMoveClipboard,
        clearCopyMoveClipboard,
        fetchLatestFileList,
        openUploadModal,
        handleUploadModalOpen,
        handleUploadModalClose,
        isAdmin,
        isExecutive,
        isUser,
        handleDelete,
        setIsRenamingFileDir,
        pending,
        setPending,
        allowClientAccessPoint,
        hasWritePermission,
    } = FileMgrProps;

    const { openContextMenu, closeContextMenu, showContextMenu, anchorEl } =
        useContextMenu();
    const {
        openDialog: isOpenPermissionsDialog,
        handleDialogOpen: handlePermissionsDialogOpen,
        handleDialogClose: handlePermissionsDialogClose,
    } = useDialog();
    const {
        openDialog: isOpenDeleteDialog,
        handleDialogOpen: handleDeleteDialogOpen,
        handleDialogClose: handleDeleteDialogClose,
    } = useDialog();
    const {
        openDialog: isOpenClientAccessPoint,
        handleDialogOpen: handleOpenClientAccessPoint,
        handleDialogClose: handleCloseClientAccessPoint,
    } = useDialog();

    const onCloseFilePreview = () => {
        setOpenFilePreview(false);
        setActiveDoc(undefined);
    };

    const goBackDirectory = (dir = undefined) => {
        setActiveDoc(undefined);
        if (dir) {
            traverseBackDirectory(dir);
        } else {
            traverseBackDirectory();
        }
    };

    const downloadFile = async (fileId) => {
        if (fileId) {
            const url = `/api/fileuploadservice/Download?FileId=${fileId}`;
            handleFileDownload(url);
        }
    };

    const downloadCheckedFiles = async () => {
        const checkedFileList = checked?.files;
        checkedFileList.forEach((file) => {
            downloadFile(file.vv_file_id);
        });
    };

    // New handler for Upload File(s) with inline permission check
    const handleUpload = async () => {
        console.log("Upload - User Info:", userInfo?.userInfo);
        console.log(
            "Upload - isAdmin:",
            isAdmin,
            "isExecutive:",
            isExecutive,
            "isCanShareAdmin:",
            userInfo?.userInfo?.isCanShareAdmin
        );
        // Only run the permission check if the user is NOT an admin, executive, or canShareAdmin.
        if (
            !(
                isAdmin ||
                isExecutive ||
                Boolean(userInfo?.userInfo?.isCanShareAdmin)
            )
        ) {
            try {
                const permsResponse = await getDirectoryPermissions(
                    currDirId,
                    custId
                );
                let permissionsArray = [];
                if (Array.isArray(permsResponse)) {
                    permissionsArray = permsResponse;
                } else if (
                    permsResponse.data &&
                    Array.isArray(permsResponse.data)
                ) {
                    permissionsArray = permsResponse.data;
                }
                console.log("Upload - Permissions Array:", permissionsArray);
                // Log each permission's UserId comparison with current userId
                permissionsArray.forEach((perm, idx) => {
                    console.log(
                        `Upload - Index ${idx}: Comparing Permission UserId "${perm.UserId}" to current userId "${userId}"`,
                        String(perm.UserId).trim() === String(userId).trim()
                    );
                });
                const userPerm = permissionsArray.find(
                    (perm) =>
                        String(perm.UserId).trim() === String(userId).trim()
                );
                console.log("Upload - User Permission Object:", userPerm);
                if (
                    !userPerm ||
                    userPerm.cbRead !== "1" ||
                    userPerm.cbWrite !== "1"
                ) {
                    notifyError(
                        "You do not have read/write permission to upload files."
                    );
                    return;
                }
            } catch (error) {
                console.error("Upload - Error during permission check:", error);
                notifyError("Error checking your permissions for upload.");
                return;
            }
        } else {
            console.log(
                "Upload - Bypassing permission check for admin/executive/canShareAdmin user."
            );
        }
        // Open the upload modal if permission check passes.
        handleUploadModalOpen();
    };

    // Updated addNewFolder function with inline check that bypasses for admin users.
    const addNewFolder = async () => {
        console.log("User Info:", userInfo?.userInfo);
        console.log(
            "isAdmin:",
            isAdmin,
            "isExecutive:",
            isExecutive,
            "isCanShareAdmin:",
            userInfo?.userInfo?.isCanShareAdmin
        );

        // Only run the permission check if the user is NOT an admin, executive, or canShareAdmin.
        if (
            !(
                isAdmin ||
                isExecutive ||
                Boolean(userInfo?.userInfo?.isCanShareAdmin)
            )
        ) {
            try {
                const permsResponse = await getDirectoryPermissions(
                    currDirId,
                    custId
                );
                let permissionsArray = [];
                if (Array.isArray(permsResponse)) {
                    permissionsArray = permsResponse;
                } else if (
                    permsResponse.data &&
                    Array.isArray(permsResponse.data)
                ) {
                    permissionsArray = permsResponse.data;
                }
                console.log("Permissions Array:", permissionsArray);
                // Log each permission's UserId comparison with current userId
                permissionsArray.forEach((perm, idx) => {
                    console.log(
                        `Index ${idx}: Comparing Permission UserId "${perm.UserId}" to current userId "${userId}"`,
                        String(perm.UserId).trim() === String(userId).trim()
                    );
                });
                const userPerm = permissionsArray.find(
                    (perm) =>
                        String(perm.UserId).trim() === String(userId).trim()
                );
                console.log("User Permission Object:", userPerm);
                if (
                    !userPerm ||
                    userPerm.cbRead !== "1" ||
                    userPerm.cbWrite !== "1"
                ) {
                    notifyError(
                        "You do not have read/write permission to add new folders."
                    );
                    return;
                }
            } catch (error) {
                console.error("Error during permission check:", error);
                notifyError("Error checking your permissions.");
                return;
            }
        } else {
            console.log(
                "Bypassing permission check for admin/executive/canShareAdmin user."
            );
        }

        const res = await createNewFolder(currDirId, custId);
        console.log("Create New Folder Response:", res);
        if (res.Success) {
            const newDir = res.data;
            setFileList((oldFileList) => [newDir, ...oldFileList]);
            notifySuccess(res.Message || "Successfully created directory.");
            setIsRenamingFileDir(newDir);
            if (
                isAdmin ||
                isExecutive ||
                Boolean(userInfo?.userInfo?.isCanShareAdmin)
            ) {
                try {
                    const permResponse = await getDirectoryPermissions(
                        newDir.vv_dir_id,
                        custId
                    );
                    const permissionsArray = permResponse.data
                        ? permResponse.data
                        : Array.isArray(permResponse)
                        ? permResponse
                        : [];
                    console.log(
                        "New Directory Permissions Array:",
                        permissionsArray
                    );
                    const defaultPermissions = permissionsArray.map(
                        (userPerm) => ({
                            ...userPerm,
                            cbRead: "1",
                            cbWrite: "1",
                        })
                    );
                    console.log(
                        "Default Permissions Payload:",
                        defaultPermissions
                    );
                    const payload = {
                        dirId: newDir.vv_dir_id,
                        custId,
                        permissions: defaultPermissions,
                    };
                    const setPermResponse = await setDirectoryPermissions(
                        payload
                    );
                    console.log(
                        "Set Default Permissions Response:",
                        setPermResponse
                    );
                    if (setPermResponse.Success) {
                        notifySuccess(
                            "Default permissions set successfully for all users."
                        );
                    } else {
                        notifyError(
                            setPermResponse.Message ||
                                "Failed to update default permissions."
                        );
                    }
                } catch (permError) {
                    console.error(
                        "Error updating default permissions:",
                        permError
                    );
                    notifyError("Error updating default permissions.");
                }
            }
        } else {
            console.error("Folder creation error:", res);
            notifyError(res.Message || "Error creating directory");
        }
    };

    const handlePaste = async () => {
        setPending(true);
        const successful = [];
        const failed = [];
        const movePromises = copyMoveClipboard?.map(async (item) => {
            if (item.isDir === 1) {
                const res = await moveDirectory(
                    item.vv_dir_id,
                    currDirId,
                    item.name
                );
                if (res.Success) {
                    successful.push(item);
                } else {
                    failed.push(item);
                }
            } else {
                const res = await moveFileToDirectory(
                    item.vv_file_id,
                    currDirId,
                    item.name
                );
                if (res.Success) {
                    successful.push(item);
                } else {
                    failed.push(item);
                }
            }
        });
        await Promise.all([...movePromises]);
        let summaryMessage = "";
        if (successful.length > 0) {
            summaryMessage += `Successfully moved ${successful.length} files/directories. `;
        }
        if (failed.length > 0) {
            summaryMessage += `Failed to move ${
                failed.length
            } directories: ${failed.map((dir) => dir.name).join(", ")}. `;
        }
        if (successful.length > 0 && failed.length === 0) {
            notifySuccess(summaryMessage);
        } else if (successful.length > 0 && failed.length > 0) {
            notifyWarning(summaryMessage);
        } else {
            notifyError(summaryMessage);
        }
        clearCopyMoveClipboard();
        setPending(false);
        fetchLatestFileList();
    };

    useEffect(() => {
        setSearchTerm("");
        setIsRenamingFileDir(undefined);
    }, [currDirId]);

    const getActions = (iconSize = "medium") => {
        const adminActions = [
            {
                text: "Folder Security",
                handler: handlePermissionsDialogOpen,
                icon: <SecurityOutlinedIcon size={iconSize} />,
                disabled: historyRef.current.length <= 1,
            },
        ];
        // New Folder and Upload actions are disabled if the user does not have write permission.
        const userActions = [
            {
                text: "New Folder",
                handler: addNewFolder,
                icon: <CreateNewFolderIcon size={iconSize} />,
                disabled: !hasWritePermission,
            },
            {
                text: "Upload File(s)",
                handler: handleUpload, // Use our new upload handler
                icon: <UploadFileOutlinedIcon size={iconSize} />,
                disabled: !hasWritePermission,
            },
            {
                text: "Rename",
                handler: () => {
                    if (checked?.files.length === 1) {
                        setIsRenamingFileDir(checked?.files[0]);
                    } else if (checked?.dir.length === 1) {
                        setIsRenamingFileDir(checked?.dir[0]);
                    }
                },
                icon: <DriveFileRenameIcon size={iconSize} />,
                disabled: checked?.files.length + checked?.dir.length !== 1,
            },
            {
                text: "Cut",
                handler: () => addFileToCopyMoveClipboard("move"),
                icon: <ContentCutIcon size={iconSize} />,
                disabled:
                    checked?.files.length <= 0 && checked?.dir.length <= 0,
            },
            {
                text: "Paste",
                handler: handlePaste,
                icon: <ContentCopyIcon size={iconSize} />,
                disabled: !copyMoveAction || copyMoveClipboard.length === 0,
            },
        ];
        const viewOnlyActions = [
            {
                text: "Download Checked Files",
                handler: downloadCheckedFiles,
                icon: <DownloadIcon size={iconSize} />,
                disabled: checked?.files.length <= 0,
            },
        ];
        if (isAdmin || isExecutive) {
            return [
                ...userActions,
                {
                    text: "Delete Selected Items",
                    handler: handleDeleteDialogOpen,
                    icon: <DeleteIcon size={iconSize} />,
                    disabled:
                        checked?.files.length <= 0 && checked?.dir.length <= 0,
                },
                ...viewOnlyActions,
                ...adminActions,
            ];
        } else if (isUser) {
            return [...userActions, ...viewOnlyActions];
        } else {
            return [...viewOnlyActions];
        }
    };

    return (
        <Box px={2} pb={0} pt={0} mt={8}>
            {/* {pending && <ProgressCircular />} */}
            {custId > 0 && (
                <>
                    <Grid
                        container
                        spacing={8}
                        onContextMenu={(e) => openContextMenu(e)}
                    >
                        <>
                            <Grid item xs={12} className={styles.toolbar_grid}>
                                <Toolbar className={styles.toolbar}>
                                    <Tooltip title="Back">
                                        <IconButton
                                            edge="start"
                                            color="inherit"
                                            onClick={() => goBackDirectory()}
                                            className={styles.toolbar_button}
                                            disabled={
                                                historyRef.current.length <= 1
                                            }
                                        >
                                            <ArrowBackIcon fontSize="medium" />
                                        </IconButton>
                                    </Tooltip>
                                    <FileMgrBreadcrumb
                                        navHistoryStack={historyRef.current}
                                        traverseBackDirectory={goBackDirectory}
                                    />
                                    {getActions("small").map((action, key) => {
                                        return (
                                            <Tooltip
                                                title={action.text}
                                                key={key}
                                            >
                                                <IconButton
                                                    edge="end"
                                                    color="inherit"
                                                    className={
                                                        styles.action_button
                                                    }
                                                    onClick={action.handler}
                                                    disabled={
                                                        action.disabled || false
                                                    }
                                                >
                                                    {action.icon}
                                                </IconButton>
                                            </Tooltip>
                                        );
                                    })}
                                    <div className={styles.right_action_button}>
                                        <TextField
                                            value={searchTerm}
                                            onChange={handleSearch}
                                            variant="outlined"
                                            size="small"
                                            label="Search Files"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Cancel">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    setSearchTerm(
                                                                        ""
                                                                    )
                                                                }
                                                            >
                                                                <ClearIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        {allowClientAccessPoint && (
                                            <Tooltip title={"Change Customer"}>
                                                <IconButton
                                                    edge="start"
                                                    color="inherit"
                                                    onClick={
                                                        handleOpenClientAccessPoint
                                                    }
                                                    className={
                                                        styles.action_button
                                                    }
                                                >
                                                    <SettingsOutlinedIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip
                                            title={
                                                tileMode
                                                    ? "List View"
                                                    : "Grid View"
                                            }
                                        >
                                            <IconButton
                                                edge="start"
                                                color="inherit"
                                                onClick={() =>
                                                    setTileMode(!tileMode)
                                                }
                                                className={styles.action_button}
                                            >
                                                {tileMode ? (
                                                    <ViewListOutlinedIcon />
                                                ) : (
                                                    <GridViewOutlinedIcon />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                </Toolbar>
                                <Divider />
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                sm={12}
                                className={styles.file_manager_grid}
                            >
                                <FileManager
                                    activeDoc={activeDoc}
                                    setActiveDoc={(file) => {
                                        setOpenFilePreview(true);
                                        setActiveDoc(file);
                                    }}
                                    FileMgrProps={FileMgrProps}
                                    tileMode={tileMode}
                                    searchTerm={searchTerm}
                                    handleDelete={handleDeleteDialogOpen}
                                />
                            </Grid>
                            {activeDoc && (
                                <Viewer
                                    doc={activeDoc}
                                    open={openFilePreview}
                                    onClose={onCloseFilePreview}
                                />
                            )}
                        </>
                    </Grid>
                    {showContextMenu && (
                        <ActionMenu
                            open={showContextMenu}
                            handleClose={closeContextMenu}
                            anchorEl={anchorEl}
                            actions={getActions("small")}
                        />
                    )}
                    <UppyDashboard
                        metaData={{
                            CustId: custId,
                            DirId: currDirId,
                            UserId: userId,
                        }}
                        open={openUploadModal}
                        handleClose={handleUploadModalClose}
                        onSingleUploadFailure={() => {}}
                        onSingleUploadSuccess={fetchLatestFileList}
                    />
                    <Permissions
                        dirId={currDirId}
                        custId={custId}
                        openDialog={isOpenPermissionsDialog}
                        onCloseDialog={handlePermissionsDialogClose}
                        historyRef={historyRef}
                    />
                    <DeleteDialog
                        openDialog={isOpenDeleteDialog}
                        onCloseDialog={handleDeleteDialogClose}
                        checkedFileDirList={checked}
                        userInfo={userInfo}
                        notifyError={notifyError}
                        notifySuccess={notifySuccess}
                        fetchLatestFileList={fetchLatestFileList}
                    />
                </>
            )}
            {custId === 0 && (
                <Grid
                    container
                    direction="column"
                    alignItems="center"
                    spacing={2}
                >
                    <Grid item>
                        <Typography variant="h5" gutterBottom>
                            Client Access Point
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography variant="body1" gutterBottom>
                            You must select a customer.
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleOpenClientAccessPoint}
                        >
                            Select Customer
                        </Button>
                    </Grid>
                </Grid>
            )}
            <>
                <ClientAccessPoint
                    openDialog={isOpenClientAccessPoint}
                    onCloseDialog={handleCloseClientAccessPoint}
                    setPending={setPending}
                    custId={custId}
                    setCustId={setCustId}
                />
            </>
        </Box>
    );
};

export default CanShare;
